import { Injectable } from '@angular/core';
import { BurialSocietiesApiService } from '@ecosure-suite/shared/data-access';
import { LogService } from '@ecosure-suite/shared/util-logging';
import { NotificationsService } from '@ecosure-suite/shared/utils-notifications';
import { BurialSocietyDocumentsConfiguration } from './tokens';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Parameter, CommonService } from '@ecosure-suite/shared/utils-helpers';
import { DocumentForm, Document } from './+state/documents.models';

@Injectable({
  providedIn: 'root'
})
export class DocumentsService {
  constructor(
    private apiService: BurialSocietiesApiService,
    private logService: LogService,
    private notificationsService: NotificationsService,
    private configuration: BurialSocietyDocumentsConfiguration
  ) {}

  getDocumentsByOwner(owner: Parameter): Observable<any> {
    return this.apiService
      .get(`/documents?ownerId=${owner.value}&ownerType=${owner.name}`)
      .pipe(
        map(data => {
          if (data.statusCode !== '200' && data.statusCode !== '404') {
            this.notificationsService.showError(
              'Documents error',
              data.message
            );
            this.logService.error(data.message);
            throw new Error(data.message);
          }

          return data.statusCode !== '404'
            ? data.responseBody.map(document => ({
                id: document.documentId,
                type: document.documentType,
                owner: { name: document.ownerType, value: document.ownerId },
                format: CommonService.getFileFormat(document.format),
                ext: document.format,
                uri: document.relativeUrl
                  ? this.configuration.url + document.relativeUrl
                  : null,
                dateCreated: new Date(document.dateCreated)
              }))
            : [];
        })
      );
  }

  saveDocumentInformation(form: DocumentForm): Observable<DocumentForm> {
    return this.apiService
      .post(`/documents`, {
        ownerId: form.owner.value,
        ownerType: form.owner.name,
        documentName: form.file.name,
        documentType: form.type,
        format: form.file.name.split('.')[1].toUpperCase(),
        createdBy: 'kkmarutsi'
      })
      .pipe(
        map(data => {
          if (data.statusCode !== '200') {
            this.notificationsService.showError(
              'Documents error',
              data.message
            );
            this.logService.error(data.message);
            throw new Error(data.message);
          }

          return {
            ...form,
            document: {
              id: data.responseBody.documentId,
              type: data.responseBody.documentType,
              owner: {
                name: data.responseBody.ownerType,
                value: data.responseBody.ownerId
              },
              format: CommonService.getFileFormat(data.responseBody.format),
              ext: data.responseBody.format,
              uri: data.responseBody.relativeUrl
                ? this.configuration.url + data.responseBody.relativeUrl
                : null,
              dateCreated: new Date(data.responseBody.dateCreated)
            }
          };
        })
      );
  }

  updateDocumentInformation(form: DocumentForm): Observable<DocumentForm> {
    return this.apiService
      .put(`/documents`, {
        documentId: form.document.id,
        ownerId: form.owner.value,
        ownerType: form.owner.name,
        documentName: form.file.name,
        documentType: form.type,
        format: form.file.name.split('.')[1].toUpperCase()
      })
      .pipe(
        map(data => {
          if (data.statusCode !== '200') {
            this.notificationsService.showError(
              'Documents error',
              data.message
            );
            this.logService.error(data.message);
            throw new Error(data.message);
          }

          return {
            ...form,
            document: {
              id: data.responseBody.documentId,
              type: data.responseBody.documentType,
              owner: {
                name: data.responseBody.ownerType,
                value: data.responseBody.ownerId
              },
              format: CommonService.getFileFormat(data.responseBody.format),
              ext: data.responseBody.format,
              uri: data.responseBody.relativeUrl
                ? this.configuration.url + data.responseBody.relativeUrl
                : null,
              dateCreated: new Date(data.responseBody.dateCreated)
            }
          };
        })
      );
  }

  upload(form: DocumentForm): Observable<Document> {
    return this.apiService
      .file(`/documents/upload?documentId=${form.document.id}`, form.file)
      .pipe(
        map(data => {
          if (data.statusCode !== '200') {
            this.notificationsService.showError(
              'Documents error',
              data.message
            );
            this.logService.error(data.message);
            throw new Error(data.message);
          }
          return {
            id: data.responseBody.documentId,
            type: data.responseBody.documentType,
            owner: {
              name: data.responseBody.ownerType,
              value: data.responseBody.ownerId
            },
            format: CommonService.getFileFormat(data.responseBody.format),
            ext: data.responseBody.format,
            uri: data.responseBody.relativeUrl
              ? this.configuration.url + data.responseBody.relativeUrl
              : null,
            dateCreated: new Date(data.responseBody.dateCreated)
          };
        })
      );
  }
}
